/**
 * LogRocket session replay: init and identify behind REACT_APP_LOGROCKET_APP_ID.
 * When the env var is not set, no-ops so the SDK is never loaded.
 */

let initialized = false;

function getAppId() {
  const id = process.env.REACT_APP_LOGROCKET_APP_ID;
  return typeof id === 'string' && id.trim() !== '' ? id.trim() : null;
}

function getLogRocketSdk() {
  try {
    return require('logrocket');
  } catch (e) {
    return null;
  }
}

export function isLogRocketConfigured() {
  return getAppId() !== null;
}

/**
 * Defers Redux event capture until LogRocket is explicitly initialized.
 * This avoids buffering pre-login Redux traffic, since this app starts
 * LogRocket only after a successful login.
 */
export function createDeferredLogRocketReduxMiddleware(options = {}) {
  let middlewareForStore = null;
  let handlerForNext = null;

  return (store) => (next) => (action) => {
    if (!initialized) {
      return next(action);
    }

    if (!middlewareForStore) {
      const LogRocket = getLogRocketSdk();

      if (!LogRocket || typeof LogRocket.reduxMiddleware !== 'function') {
        return next(action);
      }

      middlewareForStore = LogRocket.reduxMiddleware(options)(store);
    }

    if (!handlerForNext) {
      handlerForNext = middlewareForStore(next);
    }

    return handlerForNext(action);
  };
}

/**
 * Initializes LogRocket when REACT_APP_LOGROCKET_APP_ID is set.
 * Call after successful login so session replay is recorded only from then (not before login).
 */
export function initLogRocket() {
  const appId = getAppId();
  if (!appId || initialized) return;
  try {
    const LogRocket = getLogRocketSdk();
    if (!LogRocket) return;
    LogRocket.init(appId);
    initialized = true;
  } catch (e) {
    // LogRocket not installed or init failed; leave initialized false
  }
}

/**
 * Identifies the current user in LogRocket. No-op if LogRocket was not initialized
 * or userId is falsy.
 * @param {string} userId - Stable user identifier (e.g. StudentID, id).
 * @param {Object} [traits] - Optional traits (name, email, tenant_id, etc.).
 */
export function identifyUser(userId, traits) {
  if (!initialized || !userId) return;
  try {
    const LogRocket = getLogRocketSdk();
    if (!LogRocket) return;
    LogRocket.identify(userId, traits || {});
  } catch (e) {
    // ignore
  }
}

/**
 * Ends the current LogRocket session. Call on logout so the logged-in session is
 * closed and uploaded. Uses LogRocket's startNewSession() to end the current session.
 * No-op if LogRocket was not initialized.
 */
export function shutdownLogRocketSession() {
  if (!initialized) return;
  try {
    const LogRocket = getLogRocketSdk();
    if (!LogRocket) return;
    LogRocket.startNewSession();
  } catch (e) {
    // ignore
  }
}
